import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import { updateUser } from "./utils/userManagementApi";
import logger from "./utils/logger";
import "./EditUserModal.css";
import "./AddUserModal.css"; // Reuse form styles

const EditUserModal = ({ user, onClose, onUserUpdated }) => {
  const { getAccessToken } = useAuth();

  const [formData, setFormData] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    phoneNumber: user.phoneNumber || "",
    role: user.role || "Site Admin",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRoleWarning, setShowRoleWarning] = useState(false);
  const [typedConfirmation, setTypedConfirmation] = useState("");

  const roleChanged = formData.role !== user.role;
  const isChangingToFullAdmin = roleChanged && formData.role === "Full Admin";
  const isChangingFromFullAdmin = roleChanged && user.role === "Full Admin";
  const isAdminRoleChange = isChangingToFullAdmin || isChangingFromFullAdmin;
  const requiredConfirmationText = isChangingToFullAdmin
    ? "Full Admin"
    : "Not an Admin";

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, loading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Show warning if role is changing
    if (name === "role" && value !== user.role) {
      setShowRoleWarning(true);
    } else if (name === "role" && value === user.role) {
      setShowRoleWarning(false);
    }
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Optional field
    const re = /^\+\d{1,15}$/;
    return re.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.firstName || !formData.lastName) {
      setError("First name and last name are required");
      return;
    }

    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      setError("Phone number must be in E.164 format (e.g., +12025551234)");
      return;
    }

    // Check typed confirmation for admin role changes
    if (isAdminRoleChange && typedConfirmation !== requiredConfirmationText) {
      const requiredText = isChangingToFullAdmin
        ? "Full Admin"
        : "Not an Admin";
      setError(
        `Please type '${requiredText}' to proceed with this role change.`
      );
      return;
    }

    try {
      setLoading(true);

      // Build update payload (only changed fields)
      const updates = {};

      if (formData.firstName !== user.firstName) {
        updates.firstName = formData.firstName;
      }
      if (formData.lastName !== user.lastName) {
        updates.lastName = formData.lastName;
      }
      if (formData.phoneNumber !== user.phoneNumber) {
        updates.phoneNumber = formData.phoneNumber || null;
      }
      if (formData.role !== user.role) {
        updates.role = formData.role;
      }

      if (Object.keys(updates).length === 0) {
        setError("No changes detected");
        setLoading(false);
        return;
      }

      await updateUser(user.userId, updates, getAccessToken);
      logger.info("User updated successfully", {
        userId: user.userId,
        updates,
      });
      await onUserUpdated();
    } catch (err) {
      logger.error("Failed to update user", err);
      setError(err.message || "Failed to update user");
      setLoading(false);
    }
  };

  return (
    <div className="edit-user-modal-overlay">
      <div className="edit-user-modal">
        <h3>Edit User</h3>

        {error && <div className="edit-user-message error">{error}</div>}

        {showRoleWarning && (
          <div className="edit-user-message warning">
            <strong>Warning:</strong> Changing this user's role will affect
            their permissions.
            {user.organizationCount > 0 && roleChanged && (
              <>
                {" "}
                If changing from Organization Admin to Site Admin, their
                organization assignments will be removed.
              </>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="edit-user-form">
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={user.email}
              disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }}
            />
            <span className="form-helper-text">Email cannot be changed</span>
          </div>

          <div className="form-group">
            <label>
              First Name<span className="required">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>
              Last Name<span className="required">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="+12025551234"
              disabled={loading}
            />
            <span className="form-helper-text">
              Optional. Must be in E.164 format (e.g., +12025551234)
            </span>
          </div>

          <div className="form-group">
            <label>
              Role<span className="required">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="Site Admin">Site Admin</option>
              <option value="Organization Admin">Organization Admin</option>
              <option value="Full Admin">Full Admin</option>
            </select>
            <span className="form-helper-text">
              Site Admin: Basic access. Org Admin: Can manage assigned
              organizations. Full Admin: Full system access.
            </span>
          </div>

          {isAdminRoleChange && (
            <div className="form-group">
              <label>
                Type "{requiredConfirmationText}" to proceed with this role
                change:<span className="required">*</span>
              </label>
              <input
                type="text"
                className="role-confirmation-input"
                value={typedConfirmation}
                onChange={(e) => setTypedConfirmation(e.target.value)}
                placeholder={requiredConfirmationText}
                disabled={loading}
              />
              <span className="form-helper-text" style={{ color: "#d32f2f" }}>
                ⚠️{" "}
                {isChangingToFullAdmin
                  ? "Granting Full Admin privileges"
                  : "Removing Full Admin privileges"}{" "}
                affects critical system permissions. Type "
                {requiredConfirmationText}" to proceed.
              </span>
            </div>
          )}

          <div className="edit-user-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={
                loading ||
                (isAdminRoleChange &&
                  typedConfirmation !== requiredConfirmationText)
              }
            >
              {loading ? "Updating..." : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
